import { Injectable } from '@angular/core';
import { Device } from '@ionic-native/device/ngx';
import { SunmiDeviceModel, SunmiPrinterWidth } from './sunmi.service.model';
import { LocalStorage } from '@pos-common/services/utils/localstorage.utils';
import { StorageKeys } from '@pos-common/constants';

@Injectable()
export abstract class ISunmiService {
  abstract get isSunmiDevice(): boolean;
  abstract get sunmiDeviceModel(): SunmiDeviceModel;
  abstract get hasPrinter(): boolean;
  abstract get hasAppScanner(): boolean;
  abstract get hasBroadcastScanner(): boolean;
  abstract get printerWidth(): SunmiPrinterWidth;
  abstract get printerModelD3Mini(): SunmiPrinterWidth;
}

@Injectable()
export class SunmiService implements ISunmiService {
  private readonly _isSunmiDevice: boolean;
  private readonly _sunmiDeviceModel: SunmiDeviceModel;

  constructor(private device: Device, private localStorage: LocalStorage) {
    let manufacturer = this.device?.manufacturer || '';
    manufacturer = manufacturer.toLowerCase();
    this._isSunmiDevice = manufacturer === 'sunmi';
    this._sunmiDeviceModel = this.detectSunmiModel();
  }

  private detectSunmiModel(): SunmiDeviceModel {
    let model = this.device?.model || '';
    model = model.toLocaleLowerCase();

    if (model.includes('v2splus')) return SunmiDeviceModel.V2sPlus;
    if (model.includes('v2s')) return SunmiDeviceModel.V2s;
    if (model.includes('t2s')) return SunmiDeviceModel.T2s;
    if (model.includes('d3mini')) return SunmiDeviceModel.D3Mini;
    if (model.includes('d3 mini')) return SunmiDeviceModel.D3Mini;

    return SunmiDeviceModel.Unknown;
  }

  get isSunmiDevice() {
    return this._isSunmiDevice;
  }

  get sunmiDeviceModel() {
    return this._sunmiDeviceModel;
  }

  get hasPrinter() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.V2s:
      case SunmiDeviceModel.V2sPlus:
      case SunmiDeviceModel.D3Mini:
        return true;
      case SunmiDeviceModel.Unknown:
        return false;
    }
  }

  get hasAppScanner() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.V2s:
      case SunmiDeviceModel.V2sPlus:
      case SunmiDeviceModel.D3Mini:
        return true;
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.Unknown:
        return false;
    }
  }

  get hasBroadcastScanner() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.V2s:
      case SunmiDeviceModel.V2sPlus:
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.D3Mini:
        return true;
      case SunmiDeviceModel.Unknown:
        return false;
    }
  }

  get printerWidth() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.T2s:
        return SunmiPrinterWidth.w80mm;
      case SunmiDeviceModel.V2s:
        return SunmiPrinterWidth.w40mm;
      case SunmiDeviceModel.D3Mini:
        return this.printerModelD3Mini
      default:
        return SunmiPrinterWidth.w58mm;
    }
  }

  get printerModelD3Mini(): SunmiPrinterWidth {
    const rawModel: string = this.localStorage.getObject(StorageKeys.sunmiPrinterInfo)?.model;

    if (!rawModel) {
      return SunmiPrinterWidth.w80mm;
    }
    //D3 MINI 80
    const model: string = rawModel.replace(/\s+/g, "").toLowerCase();

    return model.includes('80') ? SunmiPrinterWidth.w80mm : SunmiPrinterWidth.w58mm;
  }
}
